import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, FilePlus, Building2, Users, HelpCircle, LogOut, Bug, Mail, Contact } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { LAYOUT, NAV } from '@/constants/testIds';
import { canManageUsers, isAdminNational } from '@/constants/permits';
import OfflineIndicator from './OfflineIndicator';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = isAdminNational(user);
  const isHseSite = !!user && (user.roles || []).includes('responsable_hse_site');

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', testid: NAV.dashboard },
    { to: '/permits', icon: FileText, label: 'Autorisations', testid: NAV.permits },
    { to: '/permits/new', icon: FilePlus, label: 'Nouveau permis', testid: NAV.permitNew },
    // Sites: Admin National (CRUD complet) ou Responsable HSE Site (vue restreinte)
    ...((isAdmin || isHseSite) ? [{ to: '/sites', icon: Building2, label: 'Sites', testid: NAV.sites }] : []),
    ...(isAdmin ? [{ to: '/contacts', icon: Contact, label: 'Contacts Lafarge', testid: 'nav-contacts' }] : []),
    ...(canManageUsers(user) ? [{ to: '/users', icon: Users, label: 'Utilisateurs', testid: NAV.users }] : []),
    ...(user && !(user.roles || []).every((r) => r === 'charge_travaux_lafarge' || r === 'charge_travaux_ee') ? [{ to: '/notifications', icon: Mail, label: 'Notifications', testid: 'nav-notifications' }] : []),
    { to: '/help', icon: HelpCircle, label: 'Aide', testid: NAV.help },
    { to: '/bugs', icon: Bug, label: 'Bugs / Évolutions', testid: NAV.bugs },
  ];

  // Note: les contrôles d'accès route-level restent en place — la sidebar n'est qu'un raccourci de navigation

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Sidebar */}
      <aside
        data-testid={LAYOUT.sidebar}
        className="w-64 bg-[#09090B] text-white flex flex-col fixed inset-y-0 left-0 z-30"
      >
        <div className="px-6 py-6 border-b border-stone-700">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-left w-full"
          >
            <div className="text-2xl font-display font-black tracking-tighter text-white">
              LAFARGE
            </div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 mt-0.5">
              Autorisations & Permis
            </div>
          </button>
          <div className="mt-4 h-0.5 bg-[#DA291C] w-12"></div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              end={item.to === '/permits'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-colors border-l-2 ${
                  isActive
                    ? 'bg-stone-800 text-white border-[#DA291C]'
                    : 'text-stone-400 hover:bg-stone-800 hover:text-white border-transparent'
                }`
              }
            >
              <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
              <span className="text-[12px]">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-stone-700 p-4">
          <div className="flex items-center gap-3 mb-3" data-testid={LAYOUT.userMenu}>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-stone-600" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#DA291C] flex items-center justify-center font-bold text-white text-sm">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid={LAYOUT.logoutButton}
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-stone-600 text-stone-300 hover:bg-[#DA291C] hover:border-[#DA291C] hover:text-white text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col">
        <header className="bg-white border-b-2 border-stone-200 px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <div className="h-6 w-1 bg-[#DA291C]"></div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Lafarge Ciments France</div>
              <div className="text-sm font-display font-bold text-[#09090B]">Plateforme de gestion des autorisations</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <OfflineIndicator />
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-10 animate-fade-in">{children}</main>
      </div>
    </div>
  );
}
